// Arlo HQ inside the Android app (android/: the site full screen through
// Chrome, plus home-screen widgets). The app opens the site with ?app=android,
// and with &link=1 until its widgets are linked; once someone is signed in
// here, the widgets are linked without anyone having to do anything.

import { useEffect } from 'react';
import { api } from './api.ts';
import { useDevice } from './hooks.ts';

const IN_APP = 'arlo:android-app';
const AUTOLINK = 'arlo:widgets-autolink';
export const APP_APK = '/arlo-hq.apk';
const PACKAGE = 'hq.arlo.widgets';

const get = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
// The app shares Chrome's storage, so "in the app" is remembered per tab (it survives reloads there, not into Chrome).
const inTab = {
  get: () => {
    try {
      return sessionStorage.getItem(IN_APP) === '1';
    } catch {
      return false;
    }
  },
  set: () => {
    try {
      sessionStorage.setItem(IN_APP, '1');
    } catch {
      /* storage unavailable */
    }
  },
};
const set = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* storage unavailable */
  }
};

/** Read (and tidy away) the app's hints in the address. Call once, before anything else reads the URL. */
export function noteAndroidApp(): void {
  const url = new URL(location.href);
  if (url.searchParams.get('app') !== 'android') return;
  inTab.set();
  if (url.searchParams.get('link') === '1') set(AUTOLINK, '1');
  url.searchParams.delete('app');
  url.searchParams.delete('link');
  history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
}

export const inAndroidApp = (): boolean => inTab.get() || document.referrer.startsWith(`android-app://${PACKAGE}`);

/** Opens the app's linking step with a one-time code — or, if the app isn't installed, downloads it. */
export function widgetLinkUrl(code: string): string {
  const server = location.origin;
  const q = `server=${encodeURIComponent(server)}&code=${encodeURIComponent(code)}`;
  return `intent://link?${q}#Intent;scheme=arlohq;package=${PACKAGE};S.browser_fallback_url=${encodeURIComponent(server + APP_APK)};end`;
}

/** Ask for a one-time code and hand it to the app. */
export async function linkWidgets(name?: string): Promise<void> {
  const r = await api<{ code: string }>('/api/widgets/link', { body: { name } });
  location.href = widgetLinkUrl(r.code);
}

/** In the app, with the widgets not yet linked: link them as soon as an owner is signed in. */
export function useWidgetAutoLink(): void {
  const d = useDevice();
  useEffect(() => {
    if (!d.key || d.guest || get(AUTOLINK) !== '1') return;
    // Once only, even if it fails: Settings → This device has a button for another go.
    set(AUTOLINK, null);
    void linkWidgets(d.account?.name).catch(() => undefined);
  }, [d.key, d.guest, d.account?.name]);
}
